/* global BigInt */
import React, { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookPlus, RotateCcw } from 'lucide-react';
import { v4 as uuid } from 'uuid';
import apiClient from '../services/apiClient';
import PracticeSettingsService from '../services/PracticeSettingsService';
import MarkdownText from './MarkdownText';
import { t } from '../i18n';
import './ReviewLevelUpDialog.css';

export function suggestReviewNoteName(name) {
  const title = String(name || '').trim();
  return /[0-9]+$/.test(title)
    ? title.replace(/[0-9]+$/, (number) => String(BigInt(number) + BigInt(1)))
    : `${title} 2`;
}

export default function ReviewLevelUpDialog({ card = null, note = null, onClose, onCreated, onOpenNote, writingRecord = null, importCard = false }) {
  const navigate = useNavigate();
  const titleId = useId();
  const dialogRef = useRef(null);
  const operation = useRef(null);
  const inFlight = useRef(false);
  const [selected, setSelected] = useState(card?.isReadingGroup && !importCard ? null : card);
  const [candidates, setCandidates] = useState([]);
  const [notes, setNotes] = useState([]);
  const [targetId, setTargetId] = useState('');
  const [newNote, setNewNote] = useState(Boolean(note));
  const [name, setName] = useState(note ? suggestReviewNoteName(note.noteTitle || note.title) : '');
  const [question, setQuestion] = useState('');
  const [questionConfirmed, setQuestionConfirmed] = useState(false);
  const [loading, setLoading] = useState(!note);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [savedNoteId, setSavedNoteId] = useState(null);
  const sourceNoteId = note?.noteId || card?.noteId;
  const importing = Boolean(writingRecord || importCard);
  const needsQuestion = !importCard && selected?.cardType === 'reading' &&
    !(selected.readingItemType === 'speech' && String(selected.front || '').trim());
  const questionStep = needsQuestion && !questionConfirmed;

  useEffect(() => {
    const previous = document.activeElement;
    dialogRef.current?.focus();
    return () => { if (previous?.isConnected) previous.focus(); };
  }, []);

  useEffect(() => {
    if (note) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([
      apiClient.getAllNotes(),
      card?.isReadingGroup && !importCard ? apiClient.getReadingGroupLevelUpCandidates(card.id, 'review') : Promise.resolve(null),
    ]).then(([allNotes, reading]) => {
      if (cancelled) return;
      const available = (allNotes || []).filter((item) => item.isEnabled === false &&
        (item.noteId || item.id) !== sourceNoteId);
      setNotes(available);
      setTargetId(available[0]?.noteId || available[0]?.id || '');
      setNewNote(!available.length);
      if (reading) setCandidates(reading.candidates || []);
    }).catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [card, note, sourceNoteId, loadAttempt, importCard]);

  const close = () => { if (!inFlight.current) onClose(); };
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') { event.stopPropagation(); close(); }
    if (event.key !== 'Tab') return;
    const controls = Array.from(dialogRef.current.querySelectorAll('button:not(:disabled),input:not(:disabled),select:not(:disabled)'));
    const first = controls[0]; const last = controls[controls.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
      event.preventDefault(); last?.focus();
    } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) {
      event.preventDefault(); first?.focus();
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (inFlight.current) return;
    if (questionStep) { if (question.trim()) setQuestionConfirmed(true); return; }
    const payload = note || newNote ? { newNoteTitle: name.trim() } : { targetNoteId: targetId };
    if (needsQuestion) payload.question = question.trim();
    const signature = JSON.stringify([note?.noteId || selected?.id, payload]);
    if (operation.current?.signature !== signature) operation.current = { signature, id: uuid() };
    payload.operationId = operation.current.id;
    inFlight.current = true; setBusy(true); setError('');
    try {
      const result = note
        ? await apiClient.levelUpNote(note.noteId, payload)
        : writingRecord ? await apiClient.saveWritingRecord(writingRecord.id, payload)
          : importCard ? await apiClient.importQuickPracticeCard(selected.id, payload)
          : await apiClient.levelUpFlashCard(selected.id, { ...payload, method: 'review' });
      setSavedNoteId(result.createdNote?.noteId || result.createdCard?.noteId || targetId);
      onCreated?.(result);
    } catch (err) { setError(err.message || t('reviewLevelUp.failed')); }
    finally { inFlight.current = false; setBusy(false); }
  };

  return <div className="review-level-up-overlay" onClick={(event) => { if (event.target === event.currentTarget) close(); }}>
    <section className="review-level-up-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}
      ref={dialogRef} tabIndex={-1} onKeyDown={handleKeyDown}>
      <header><h2 id={titleId}>{importing ? <BookPlus size={21} aria-hidden="true" /> : <RotateCcw size={21} aria-hidden="true" />} {importing ? t('quickPractice.importToNotes') : t('reviewLevelUp.title')}</h2>
        <button type="button" disabled={busy} onClick={close} aria-label={t('reviewLevelUp.close')}>×</button></header>
      {error && <p role="alert">{error}</p>}
      {savedNoteId ? <div role="status"><p>{t(importCard ? 'quickPractice.importCreated' : writingRecord ? 'writingExam.created' : 'reviewLevelUp.created')}</p>
        <button type="button" onClick={() => {
          if (onOpenNote) { void onOpenNote(savedNoteId); return; }
          onClose(); navigate('/map', { state: { noteId: savedNoteId } });
        }}>{t('reviewLevelUp.open')}</button>
      </div> : loading ? <p role="status">{t('reviewLevelUp.loading')}</p> : !note && !selected ? <div>
        <p>{t('reviewLevelUp.chooseSentence')}</p>
        {candidates.map((item) => <button className="review-sentence" type="button" key={item.id} onClick={() => setSelected(item)}>
          <MarkdownText text={item.suggestedAnswer || item.back} />
        </button>)}
        {!candidates.length && <p>{t('reviewLevelUp.empty')}</p>}
        {error && <button type="button" onClick={() => setLoadAttempt((value) => value + 1)}>{t('reviewLevelUp.retry')}</button>}
      </div> : <form onSubmit={submit}>
        {selected && !importing && <p>{t('reviewLevelUp.level', undefined, { from: PracticeSettingsService.getCardLevel(selected), to: Math.min(8, PracticeSettingsService.getCardLevel(selected) + 1) })}</p>}
        {questionStep ? <>
          <MarkdownText text={selected.back || selected.readingItemText} />
          <label>{t('reviewLevelUp.question')}<input required maxLength={500} value={question} onChange={(event) => setQuestion(event.target.value)} /></label>
          <p>{t('reviewLevelUp.questionHint')}</p>
        </> : <>
          <p>{t(importCard ? 'quickPractice.importHint' : writingRecord ? 'writingExam.saveHint' : note ? 'reviewLevelUp.noteHint' : 'reviewLevelUp.targetHint', undefined, { goal: PracticeSettingsService.getCardScoreLimit({ cardType: 'normal', level: 1 }) })}</p>
          {!note && <div className="review-destination-tabs">
            <button type="button" disabled={busy || !notes.length} aria-pressed={!newNote} onClick={() => setNewNote(false)}>{t('reviewLevelUp.existing')}</button>
            <button type="button" disabled={busy} aria-pressed={newNote} onClick={() => setNewNote(true)}>{t('reviewLevelUp.newNote')}</button>
          </div>}
          {note || newNote ? <label>{t('reviewLevelUp.name')}<input required maxLength={120} value={name} disabled={busy} onChange={(event) => setName(event.target.value)} /></label>
            : <label>{t('reviewLevelUp.destination')}<select required value={targetId} disabled={busy} onChange={(event) => setTargetId(event.target.value)}>
              {notes.map((item) => <option key={item.noteId || item.id} value={item.noteId || item.id}>{item.noteTitle || item.title}</option>)}
            </select></label>}
        </>}
        <footer><button type="submit" disabled={busy}>{t(importing ? busy ? 'writingExam.saving' : 'quickPractice.importToNotes' : busy ? 'reviewLevelUp.creating' : questionStep ? 'reviewLevelUp.next' : 'reviewLevelUp.create')}</button>
          <button type="button" disabled={busy} onClick={close}>{t('reviewLevelUp.cancel')}</button></footer>
      </form>}
    </section>
  </div>;
}
